/**
 * SharePedigreeDialog — mint, copy, and revoke the public link for a cross.
 *
 * The link is a random token anyone can open with no login, so the dialog is
 * explicit that possession of the URL is the only thing gating access.
 *
 * Props:
 *   open, onClose
 *   sire, dam: the selected horse objects (need .id and a display name)
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ConfirmDialog from './ConfirmDialog';
import { breedingAPI } from '../services/api';

const shareUrlFor = (token) => `${window.location.origin}/shared-pedigree/${token}`;

export default function SharePedigreeDialog({ open, onClose, sire, dam }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const sireId = sire?.id;
  const damId = dam?.id;

  // Look up any link that already exists for this pair each time the dialog opens
  useEffect(() => {
    if (!open || !sireId || !damId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopied(false);
    breedingAPI
      .getShare(sireId, damId)
      .then((res) => {
        if (!cancelled) setShare(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sireId, damId]);

  const handleCreate = useCallback(() => {
    setSaving(true);
    setError(null);
    breedingAPI
      .createShare({ sire_id: sireId, dam_id: damId, title, note })
      .then((res) => setShare(res.data))
      .catch((err) => {
        const msg = err.response?.data?.error || err.message;
        const hint = err.response?.data?.hint;
        setError(hint ? `${msg} — ${hint}` : msg);
      })
      .finally(() => setSaving(false));
  }, [sireId, damId, title, note]);

  const handleRevoke = useCallback(() => {
    setConfirmRevoke(false);
    setSaving(true);
    setError(null);
    breedingAPI
      .revokeShare(share.id)
      .then(() => {
        setShare(null);
        setCopied(false);
      })
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setSaving(false));
  }, [share]);

  const handleCopy = useCallback(async () => {
    const url = shareUrlFor(share.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be blocked (insecure origin, permissions). The URL
      // field is selectable, so fall back to telling the user to copy it.
      setError('Could not copy automatically — select the link above and copy it.');
    }
  }, [share]);

  const url = share ? shareUrlFor(share.token) : '';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Share this pedigree</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking for an existing link…</Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {sire?.registered_name || sire?.name} × {dam?.registered_name || dam?.name}
              </Typography>

              {error && <Alert severity="warning">{error}</Alert>}

              {share ? (
                <>
                  <Alert severity="info">
                    Anyone with this link can view the pedigree without logging in. The link
                    is the only thing protecting it — revoke it if it gets out.
                  </Alert>

                  <TextField
                    label="Share link"
                    value={url}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                            <IconButton onClick={handleCopy} edge="end" size="small">
                              {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    onFocus={(e) => e.target.select()}
                  />

                  <Box>
                    <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
                      Open the shared page <OpenInNewIcon sx={{ fontSize: 14, ml: 0.25 }} />
                    </Link>
                  </Box>

                  {share.title && (
                    <Typography variant="body2">
                      <strong>Title:</strong> {share.title}
                    </Typography>
                  )}
                  {share.note && (
                    <Typography variant="body2">
                      <strong>Note:</strong> {share.note}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    {share.view_count === 0
                      ? 'Not opened yet.'
                      : `Opened ${share.view_count} time${share.view_count === 1 ? '' : 's'}.`}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    This creates a link that anyone can open without logging in — useful for
                    sending a proposed cross to a buyer or a mare owner.
                  </Typography>
                  <TextField
                    label="Title (optional)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 200 }}
                    placeholder="Proposed 2027 cross"
                  />
                  <TextField
                    label="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder="Shown to whoever opens the link."
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {share && (
            <Button
              color="error"
              onClick={() => setConfirmRevoke(true)}
              disabled={saving}
              sx={{ mr: 'auto' }}
            >
              Revoke link
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
          {!share && !loading && (
            <Button variant="contained" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmRevoke}
        title="Revoke this link?"
        message="The existing URL will stop working immediately for everyone it was sent to. This cannot be undone — you can create a new link, but it will have a different address."
        onConfirm={handleRevoke}
        onCancel={() => setConfirmRevoke(false)}
        confirmLabel="Revoke"
        confirmColor="error"
      />
    </>
  );
}
